// CompSelection is responsible for rendering the grid and the show all/less footer.
import { noop } from '@aloft/toolkit-shared';
import { Block, Button, Flex, Typography } from '@aloft/toolkit-sui';
import { Table } from '@tanstack/react-table';

import { useMemo, useRef, useState } from 'react';

import { CompSelectionProperty, CompSelectionProps } from '../types.ts';
import { getSelectedCompStats } from '../utils/compSelectionState.ts';
import { CompSelectionGrid } from './CompSelectionGrid.tsx';
import { ContextActionBar } from './ContextActionBar.tsx';

const INITIAL_VISIBLE_COMPS = 20;

export const CompSelection = ({
  sorting,
  setSorting,
  rowSelection,
  setRowSelection,
  data,
  subject,
  comps,
  source,
  orderID,
  isInspectifyOrder,
  hasArvOption,
  compSelectionTableVariant = 'sales',
  isExpanded = false,
  totalComps,
  onExpand,
  onCollapse,
  updateCompsLabel,
  onUpdateComps,
  onDeselectAll,
}: CompSelectionProps) => {
  const compSelectionTableRef = useRef<Table<CompSelectionProperty> | undefined>();
  const [focusedRow, setFocusedRow] = useState<string | undefined>('subject');

  const dataWithPinned = [subject, ...data];
  const focusedPropertyIndex = dataWithPinned.findIndex((property) => property.id === focusedRow);

  const showFooter = totalComps !== undefined && totalComps > INITIAL_VISIBLE_COMPS;
  const hiddenCount = totalComps !== undefined ? totalComps - INITIAL_VISIBLE_COMPS : 0;

  const showPriceStats = hasArvOption && compSelectionTableVariant === 'sales';
  const selectedStats = useMemo(
    () =>
      getSelectedCompStats({
        rowSelection,
        properties: [...data, ...comps],
        isInspectifyOrder,
        hasArvOption,
      }),
    [rowSelection, data, comps, isInspectifyOrder, hasArvOption],
  );

  return (
    <Flex
      position="relative"
      flexDirection="row"
      justifyContent="space-between"
      borderWidth="1px"
      borderColor="strokeDefault"
      borderStyle="solid"
      height="100%"
    >
      <Block flex={1} minWidth={0} height="100%">
        <Flex flexDirection="column" height="100%">
          <Block flex={1} minWidth={0} overflow="hidden">
            <CompSelectionGrid
              ref={compSelectionTableRef}
              data={data}
              subject={subject}
              comps={comps}
              sorting={sorting}
              setSorting={setSorting}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              setFocusedRow={setFocusedRow}
              focusedPropertyIndex={focusedPropertyIndex}
              source={source}
              orderID={orderID}
              isInspectifyOrder={isInspectifyOrder}
              hasArvOption={hasArvOption}
              compSelectionTableVariant={compSelectionTableVariant}
            />
          </Block>
          {showFooter && (
            <Flex
              alignItems="center"
              gap="10px"
              padding="13px 16px"
              backgroundColor="surface1"
              flexShrink={0}
              __dangerDoNotUseSx={{
                borderTop: '1px solid',
              }}
            >
              <Button
                text={isExpanded ? 'Show less' : 'Show all'}
                variant="secondary"
                size="sm"
                onClick={isExpanded ? (onCollapse ?? noop) : (onExpand ?? noop)}
              />
              <Typography variant="body2" color="textSecondary">
                {isExpanded
                  ? 'All comparables shown'
                  : `${hiddenCount} of ${totalComps} comparables hidden`}
              </Typography>
            </Flex>
          )}
        </Flex>
      </Block>

      {onUpdateComps && onDeselectAll && updateCompsLabel && (
        <ContextActionBar
          selectedCount={selectedStats.selectedCount}
          showPriceStats={showPriceStats}
          aivAvgSalePrice={selectedStats.aivAvgSalePrice}
          arvAvgSalePrice={selectedStats.arvAvgSalePrice}
          priceDifference={selectedStats.difference}
          updateLabel={updateCompsLabel}
          onUpdate={onUpdateComps}
          onDeselectAll={onDeselectAll}
        />
      )}
    </Flex>
  );
};
